import React, { useState, useEffect } from 'react';
import { ApiClient } from '../../api/client';
import { DEPLOYMENT } from '../../config/deployment';
import { CloudflareUsagePanel, levelOf, type CfUsage } from './CloudflareUsagePanel';
import {
  Activity, RefreshCw, HardDrive, Database, ArrowUpRight, ArrowDownLeft,
  AlertTriangle, CheckCircle2, Trash2, Cloud, ShieldCheck,
  Layers, Building2, Zap, DollarSign, ExternalLink
} from 'lucide-react';

interface GlobalMonitoringViewProps {
  initialTab?: 'overview' | 'storage' | 'sync' | 'quotas' | 'tenants';
}

export const GlobalMonitoringView: React.FC<GlobalMonitoringViewProps> = ({ initialTab = 'overview' }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'storage' | 'sync' | 'quotas' | 'tenants'>(initialTab);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [syncing, setSyncing] = useState<'push' | 'pull' | null>(null);
  const [syncingMirror, setSyncingMirror] = useState(false);
  const [syncMsg, setSyncMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // Valódi Cloudflare-forgalom (külön kérés; lassabb, ezért nem várjuk meg a csempékhez)
  const [cfUsage, setCfUsage] = useState<CfUsage | null>(null);
  const [cfLoading, setCfLoading] = useState(false);
  const loadCf = async (refresh = false) => {
    setCfLoading(true);
    try {
      const r: any = await ApiClient.getCloudflareUsage(refresh);
      if (r?.ok && r.data) setCfUsage(r.data);
    } catch { /* a monitor enélkül is működik */ }
    setCfLoading(false);
  };

  const loadData = async () => {
    setLoading(true);
    loadCf();
    const res = await ApiClient.getSystemMonitoring();
    if (res.ok && res.data) {
      setData(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const formatBytes = (bytes?: number) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDeleteOrphan = async (attId: number) => {
    if (!confirm('Véglegesen törli ezt az árva fájlt? Ez a művelet visszavonhatatlanul felszabadítja az R2 tárhelyet.')) {
      return;
    }
    setDeletingId(attId);
    try {
      const res = await ApiClient.deleteAttachment(attId);
      if (res.ok) {
        await loadData();
      } else {
        alert('Hiba a fájl törlésekor: ' + (res.error || 'Ismeretlen hiba'));
      }
    } catch (e: any) {
      alert('Hiba: ' + e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleManualSync = async (type: 'push' | 'pull') => {
    setSyncing(type);
    setSyncMsg(null);
    try {
      if (type === 'pull') {
        const res = await ApiClient.pullSync(data?.sync?.latest_events?.[0]?.id || 0);
        if (res.ok) {
          setSyncMsg({ type: 'ok', text: `Sikeres Pull szinkronizáció! ${res.count || 0} esemény feldolgozva.` });
        } else {
          setSyncMsg({ type: 'err', text: `Pull hiba: ${res.error || 'Ismeretlen hiba'}` });
        }
      } else {
        const res = await ApiClient.pushSync([]);
        if (res.ok) {
          setSyncMsg({ type: 'ok', text: `Sikeres Push szinkronizáció! Események naplózva a felhőben.` });
        } else {
          setSyncMsg({ type: 'err', text: `Push hiba: ${res.error || 'Ismeretlen hiba'}` });
        }
      }
      await loadData();
    } catch (e: any) {
      setSyncMsg({ type: 'err', text: `Hálózati hiba: ${e.message}` });
    } finally {
      setSyncing(null);
    }
  };  // A gombok ma már a szinkron varázslót nyitják (kozad:open-sync); a régi eseménynapló-hívás megmaradt.
  void handleManualSync;


  const handleTriggerMirrorSync = async () => {
    setSyncingMirror(true);
    setSyncMsg(null);
    try {
      const res = await ApiClient.triggerMirrorSync();
      if (res.ok) {
        setSyncMsg({ type: 'ok', text: `Sikeres tükrözés a(z) ${DEPLOYMENT.mirrorLabel} tárhelyen! (${res.duration_ms} ms)` });
        await loadData();
      } else {
        setSyncMsg({ type: 'err', text: `Tükrözési hiba: ${res.error || 'Ismeretlen hiba'}` });
      }
    } catch (e: any) {
      setSyncMsg({ type: 'err', text: `Hálózati hiba: ${e.message}` });
    } finally {
      setSyncingMirror(false);
    }
  };

  // Egy kvótasor kihasználtsága: a valódi Cloudflare-mérés, ha van; különben az alkalmazás saját mérése; null = ismeretlen
  const quotaPercent = (item: any): number | null => {
    const cf = cfUsage && cfUsage.configured ? cfUsage : null;
    const pc = (u: number, m: number) => (m ? Math.round((u / m) * 1000) / 10 : 0);
    switch (item.key) {
      case 'workers_requests': return cf?.workers ? cf.workers.percent : null;
      case 'd1_rows_read': return cf?.d1 ? cf.d1.read_percent : null;
      case 'd1_rows_written': return cf?.d1 ? cf.d1.write_percent : null;
      case 'r2_class_a': if (cf?.r2) return cf.r2.class_a_percent; break;
      case 'r2_class_b': if (cf?.r2) return cf.r2.class_b_percent; break;
    }
    if (item.used_bytes !== undefined && item.max_bytes) return pc(Number(item.used_bytes || 0), item.max_bytes);
    if (item.used !== undefined && item.used !== null && item.max) return pc(Number(item.used), item.max);
    return null;
  };

  const storage = data?.storage || {};
  const ops = data?.ops_this_month || {};
  const db = data?.database || {};
  const sync = data?.sync || {};
  const subscription = data?.subscription || {};
  const orphans = data?.orphans || [];
  const perTenant = data?.per_tenant_storage || [];

  return (
    <div className="space-y-6">
      {/* Fejléc */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Activity className="w-7 h-7 text-indigo-600 dark:text-cyan-400" />
            <span>Rendszer- és Erőforrás Monitor</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Cloudflare R2 fájltárhely, D1 adatbázis méretek, Push / Pull szinkronizáció és előfizetési kvóták felügyelete
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Frissítés</span>
          </button>
        </div>
      </div>

      {/* KPI Felső Kártyák */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {/* 1. R2 Tárhely */}
        <div className="p-4 rounded-2xl bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 font-medium">
            <span>Fájltárhely (R2)</span>
            <HardDrive className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
          </div>
          <div className="text-xl font-extrabold text-slate-900 dark:text-white">
            {formatBytes(storage.bytes)}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between">
            <span>{(storage.files || 0).toLocaleString('hu-HU')} fájl · 10 GB keret</span>
            <span className="font-semibold text-cyan-700 dark:text-cyan-300">{storage.percent || 0}%</span>
          </div>
        </div>

        {/* 2. Írási műveletek (Class A) */}
        <div className="p-4 rounded-2xl bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 font-medium">
            <span title="Az alkalmazás saját számlálója: fájlfeltöltés, -csere, -törlés. A Cloudflare által mért Class A műveletek lent.">Fájlfeltöltések (R2)</span>
            <ArrowUpRight className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="text-xl font-extrabold text-slate-900 dark:text-white">
            {(ops.r2_write || 0).toLocaleString('hu-HU')}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between">
            <span>{ops.yyyymm} · saját számláló</span>
            <span className="font-semibold text-emerald-700 dark:text-emerald-300">{ops.writes_percent || 0}%</span>
          </div>
        </div>

        {/* 3. Olvasási műveletek (Class B) */}
        <div className="p-4 rounded-2xl bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 font-medium">
            <span title="Az alkalmazás saját számlálója: fájlletöltés, előnézet. A Cloudflare által mért Class B műveletek lent.">Fájlletöltések (R2)</span>
            <ArrowDownLeft className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="text-xl font-extrabold text-slate-900 dark:text-white">
            {(ops.r2_read || 0).toLocaleString('hu-HU')}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between">
            <span>{ops.yyyymm} · saját számláló</span>
            <span className="font-semibold text-blue-700 dark:text-blue-300">{ops.reads_percent || 0}%</span>
          </div>
        </div>

        {/* 4. D1 Adatbázis */}
        <div className="p-4 rounded-2xl bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 font-medium">
            <span>D1 Adatbázis</span>
            <Database className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="text-xl font-extrabold text-slate-900 dark:text-white">
            {formatBytes(db.size_bytes ?? db.estimated_bytes)} <span className="text-xs font-normal text-slate-500">{db.size_source === 'd1' ? 'valós méret' : 'becsült'}</span>
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between gap-2">
            <span className="truncate" title={`${db.records_count || 0} aktív bejegyzés · ${db.users_count || 0} felhasználó · ${db.pages_count || 0} oldal · ${db.tenants_count || 0} szervezet`}>
              {db.records_count || 0} bejegyzés · {db.users_count || 0} felhasználó · {db.pages_count || 0} oldal
            </span>
            <span className="font-semibold text-indigo-700 dark:text-indigo-300 shrink-0">{db.percent || 0}% / 5 GB</span>
          </div>
        </div>

        {/* 5. Szinkronizáció */}
        <div className="p-4 rounded-2xl bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 font-medium">
            <span>Szinkronizáció</span>
            <Zap className="w-4 h-4 text-amber-500" />
          </div>
          <div className="text-xl font-extrabold text-slate-900 dark:text-white">
            {sync.total_events || 0} <span className="text-xs font-normal text-slate-500">esemény</span>
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            {sync.last_sync_timestamp ? new Date(sync.last_sync_timestamp).toLocaleTimeString('hu-HU') : 'Folyamatos'}
          </div>
        </div>
      </div>

      {/* Valódi Cloudflare-forgalom (fiókszintű keret) */}
      <CloudflareUsagePanel usage={cfUsage} loading={cfLoading} onRefresh={() => loadCf(true)} />

      {/* Navigációs Fülváltó */}
      <div className="flex items-center gap-1.5 border-b border-slate-200 dark:border-slate-800 pb-2 overflow-x-auto">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer whitespace-nowrap ${
            activeTab === 'overview'
              ? 'bg-indigo-600 text-white shadow-xs'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          📊 R2 Tárhely & Műveletszámok
        </button>

        <button
          onClick={() => setActiveTab('sync')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer whitespace-nowrap ${
            activeTab === 'sync'
              ? 'bg-indigo-600 text-white shadow-xs'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          🔄 Push / Pull Szinkronizáció
        </button>

        <button
          onClick={() => setActiveTab('quotas')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer whitespace-nowrap ${
            activeTab === 'quotas'
              ? 'bg-indigo-600 text-white shadow-xs'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          💳 Előfizetés & Kvóta Figyelő
        </button>

        <button
          onClick={() => setActiveTab('tenants')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer whitespace-nowrap ${
            activeTab === 'tenants'
              ? 'bg-indigo-600 text-white shadow-xs'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          🏢 Tárhely Szervezetenként
        </button>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          TAB 1: R2 TÁRHELY & MŰVELETSZÁMOK (A SCREENSHOT ALAPJÁN)
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Fő Tárhely & Műveletszám Kártya */}
          <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-6">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <span className="text-xl">📊</span>
                <h3 className="font-bold text-base text-slate-900 dark:text-white">
                  Tárhely- és műveletszám-használat (R2)
                </h3>
              </div>
              <button
                onClick={loadData}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-semibold transition cursor-pointer"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                <span>Frissítés</span>
              </button>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Saját, a rendszerben nyomon követett becslés a Cloudflare R2 ingyenes szintjéhez képest – nem élő számlázási szinkron, de korai jelzésre megbízható. A műveletszám-számlálás a bevezetés óta gyűjt adatot, a korábbi feltöltések nincsenek benne.
            </p>

            <div className="space-y-5">
              {/* 1. Tárhely (R2) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-semibold">
                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <span>💾 Tárhely (R2)</span>
                  </div>
                  <div className="font-mono text-slate-700 dark:text-slate-300">
                    {formatBytes(storage.bytes)} / {formatBytes(storage.free_tier_bytes)} · {storage.files || 0} fájl
                  </div>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-800 h-3 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500 bg-teal-600 dark:bg-teal-500"
                    style={{ width: `${Math.max(1, Math.min(100, storage.percent || 0.5))}%` }}
                  />
                </div>
              </div>

              {/* 2. Írási műveletek (Class A) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-semibold">
                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <span>⬆ Írási műveletek ({ops.yyyymm})</span>
                  </div>
                  <div className="font-mono text-slate-700 dark:text-slate-300">
                    {(ops.r2_write || 0).toLocaleString('hu-HU')} / {(ops.free_tier_writes || 1000000).toLocaleString('hu-HU')}
                  </div>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-800 h-3 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500 bg-teal-600 dark:bg-teal-500"
                    style={{ width: `${Math.max(1, Math.min(100, ops.writes_percent || 0.2))}%` }}
                  />
                </div>
              </div>

              {/* 3. Olvasási műveletek (Class B) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-semibold">
                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <span>⬇ Olvasási műveletek ({ops.yyyymm})</span>
                  </div>
                  <div className="font-mono text-slate-700 dark:text-slate-300">
                    {(ops.r2_read || 0).toLocaleString('hu-HU')} / {(ops.free_tier_reads || 10000000).toLocaleString('hu-HU')}
                  </div>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-800 h-3 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500 bg-teal-600 dark:bg-teal-500"
                    style={{ width: `${Math.max(1, Math.min(100, ops.reads_percent || 0.1))}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Árva mellékletek szakasz (screenshot alapján) */}
            <div className="pt-6 border-t border-slate-200 dark:border-slate-800 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 dark:text-slate-400 font-bold">
                  ÁRVA MELLÉKLETEK (TÖRÖLT PONTHOZ TARTOZÓ, DE MEGMARADT FÁJLOK)
                </div>
                {orphans.length > 0 && (
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 font-bold">
                    {orphans.length} db árva fájl
                  </span>
                )}
              </div>

              <p className="text-xs text-slate-500 dark:text-slate-400">
                Ezek a fájlok olyan pontokhoz tartoznak, amiket időközben töröltek – feleslegesen foglalnak tárhelyet. Törlés előtt ellenőrizd a megőrzési kötelezettséget (van, hogy jogszabály szerint évekig meg kell őrizni még a törölt pont anyagát is).
              </p>

              {orphans.length === 0 ? (
                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800 text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center gap-2">
                  <span>Nincs árva melléklet.</span>
                  <span className="text-emerald-600 dark:text-emerald-400">✅</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {orphans.map((a: any) => (
                    <div
                      key={a.id}
                      className="p-3 rounded-xl bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="text-base">📎</span>
                        <div className="min-w-0">
                          <div className="font-bold text-slate-900 dark:text-white truncate">
                            {a.filename}
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">
                            {a.page_title} {a.tenant_name ? `· ${a.tenant_name}` : ''} · {formatBytes(a.size)} · Feltöltve: {new Date(a.uploaded_at).toLocaleDateString('hu-HU')}
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => handleDeleteOrphan(a.id)}
                        disabled={deletingId === a.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-xs font-semibold transition cursor-pointer shrink-0 disabled:opacity-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>{deletingId === a.id ? 'Törlés...' : 'Végleges törlés'}</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 2: PUSH / PULL SZINKRONIZÁCIÓ MONITOROZÁSA
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'sync' && (
        <div className="space-y-6">
          {syncMsg && (
            <div className={`p-4 rounded-2xl border text-xs flex items-center gap-2.5 ${
              syncMsg.type === 'ok'
                ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300'
                : 'bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-300'
            }`}>
              {syncMsg.type === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              <span>{syncMsg.text}</span>
            </div>
          )}

          {/* Csomópontok Állapota */}
          <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2">
                  <Cloud className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  <span>Replikációs Csomópontok & Topológia</span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Multi-Cloud & Helyi hibrid szinkronizáció valós idejű kapcsolati státusza
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.dispatchEvent(new Event('kozad:open-sync'))}
                  title="Adatok áthozása egy másik környezetből (előnézettel és jóváhagyással)"
                  disabled={syncing !== null}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-xl text-xs font-semibold transition cursor-pointer disabled:opacity-50"
                >
                  <ArrowDownLeft className={`w-3.5 h-3.5 ${syncing === 'pull' ? 'animate-spin' : ''}`} />
                  <span>Pull – áthozás</span>
                </button>

                <button
                  onClick={() => window.dispatchEvent(new Event('kozad:open-sync'))}
                  title="Adatok küldése egy másik környezetbe (előnézettel és jóváhagyással)"
                  disabled={syncing !== null}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold transition cursor-pointer disabled:opacity-50"
                >
                  <ArrowUpRight className={`w-3.5 h-3.5 ${syncing === 'push' ? 'animate-spin' : ''}`} />
                  <span>Push – küldés</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              {(sync.nodes || []).map((node: any, idx: number) => (
                <div key={idx} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 space-y-2 flex flex-col justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-900 dark:text-white">{node.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                        node.status === 'online' || node.status === 'success'
                          ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                          : node.status === 'pending'
                          ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                          : node.status === 'ready'
                          ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                          : node.status === 'not_configured'
                          ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                          : 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                      }`}>
                        {node.status_label || (node.status === 'online' || node.status === 'success' ? 'SZINKRONIZÁLVA' : node.status === 'pending' ? 'TELEPÍTÉSRE VÁR' : 'OFFLINE')}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{node.desc}</p>
                  </div>

                  {node.type === 'mirror' && node.configured !== false && (
                    <div className="pt-2.5 mt-2 flex items-center justify-between border-t border-slate-200/80 dark:border-slate-800/80 text-[11px]">
                      <button
                        onClick={handleTriggerMirrorSync}
                        disabled={syncingMirror}
                        className="text-indigo-600 dark:text-cyan-400 font-semibold hover:underline flex items-center gap-1 cursor-pointer disabled:opacity-50"
                      >
                        <RefreshCw className={`w-3 h-3 ${syncingMirror ? 'animate-spin' : ''}`} />
                        <span>{syncingMirror ? 'Szinkronizálás folyamatban...' : 'Szinkronizálás most'}</span>
                      </button>
                      {DEPLOYMENT.mirrorDashboardUrl && <a
                        href={DEPLOYMENT.mirrorDashboardUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 flex items-center gap-1 transition"
                      >
                        <span>Műszerfal</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Legutóbbi Szinkronizációs Események Táblázat */}
          <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2">
                  <Layers className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  <span>Legutóbbi Szinkronizációs Események (CDC Log)</span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Rekordok és entitások felhőbe küldött és fogadott verziói
                </p>
              </div>
              <span className="text-xs font-mono text-slate-500 dark:text-slate-400">
                Összes esemény: {sync.total_events || 0}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 uppercase">
                  <tr>
                    <th className="p-3 font-semibold">Időpont</th>
                    <th className="p-3 font-semibold">Szervezet</th>
                    <th className="p-3 font-semibold">Entitás Típusa</th>
                    <th className="p-3 font-semibold">Művelet</th>
                    <th className="p-3 font-semibold">Verzió</th>
                    <th className="p-3 font-semibold">UUID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                  {(!sync.latest_events || sync.latest_events.length === 0) ? (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-slate-500">
                        Nincs rögzített szinkronizációs esemény. A rendszer készenlétben áll.
                      </td>
                    </tr>
                  ) : (
                    sync.latest_events.map((ev: any) => (
                      <tr key={ev.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                        <td className="p-3 font-mono text-slate-600 dark:text-slate-400">
                          {ev.created_at ? new Date(ev.created_at).toLocaleString('hu-HU') : '–'}
                        </td>
                        <td className="p-3 font-medium text-slate-900 dark:text-white">
                          {ev.tenant_name || `Tenant #${ev.tenant_id}`}
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 font-mono text-[10px]">
                            {ev.entity_type}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                            {ev.action}
                          </span>
                        </td>
                        <td className="p-3 font-mono">v{ev.version}</td>
                        <td className="p-3 font-mono text-slate-400 truncate max-w-[120px]">{ev.entity_uuid}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 3: ELŐFIZETÉSEK & KVÓTA FIGYELŐ (KÖLTSÉGKONTROLL)
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'quotas' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-800">
              <div>
                <h3 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  <span>Előfizetési Szintek & Költségmegelőzési Figyelő</span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  A felhős infrastruktúra (Cloudflare R2, D1, Workers) ingyenes és díjköteles határértékei
                </p>
              </div>

              {(() => {
                const levels = (subscription.items || []).map((it: any) => levelOf(quotaPercent(it)));
                const over = levels.filter((x: string) => x === 'over').length;
                const warn = levels.filter((x: string) => x === 'warn').length;
                const unknown = levels.filter((x: string) => x === 'unknown').length;
                const cls = over ? 'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-950/40 dark:border-rose-800 dark:text-rose-300'
                  : warn ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-300'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300';
                return (
                  <div className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl border text-xs font-bold ${cls}`}>
                    <ShieldCheck className="w-4 h-4" />
                    <span>
                      {over ? `${over} keret túllépve` : warn ? `${warn} keret 80% felett` : 'A mért értékek kereten belül'}
                      {unknown ? ` · ${unknown} nincs mérve` : ''}
                    </span>
                  </div>
                );
              })()}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(subscription.items || []).map((item: any, idx: number) => (
                <div key={idx} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 dark:text-white">{item.name}</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                      Túllépési díj: {item.overage_rate}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 flex items-center justify-between gap-2">
                    <span>Ingyenes keret: <strong>{item.limit}</strong></span>
                    {(() => {
                      const p = quotaPercent(item);
                      const lv = levelOf(p);
                      if (lv === 'unknown') return <span className="text-slate-400 font-semibold" title="Ezt csak a Cloudflare méri – állítsd be a CF_ANALYTICS_TOKEN titkot">– nincs mérve</span>;
                      const cls = lv === 'over' ? 'text-rose-600' : lv === 'warn' ? 'text-amber-600' : 'text-emerald-600 dark:text-emerald-400';
                      return <span className={`${cls} font-semibold`}>{lv === 'over' ? '✗ Túllépve' : lv === 'warn' ? '⚠ 80% felett' : '✓ Kereten belül'} ({p}%)</span>;
                    })()}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-900 dark:text-amber-200 space-y-1">
              <div className="font-bold flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span>Költségkontroll Szabályzat és Biztonsági Védelem</span>
              </div>
              <p className="text-[11px] leading-relaxed">
                A rendszer proaktívan figyeli az R2 Class A műveletek számát és az R2 tárolt gigabájtokat. 80%-os kihasználtság elérésekor az adminisztrátori felület sárga figyelmeztetést jelenít meg, megelőzve a váratlan felhőszámlákat.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 4: TÁRHELY SZERVEZETENKÉNT
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'tenants' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-4">
            <div>
              <h3 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <span>Tárhely- és Rekordhasználat Szervezetenként</span>
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Minden regisztrált önkormányzat és intézmény egyedi erőforrás-kihasználtsága
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 uppercase">
                  <tr>
                    <th className="p-3 font-semibold">Szervezet Neve</th>
                    <th className="p-3 font-semibold">Azonosító (Slug)</th>
                    <th className="p-3 font-semibold">Csatolt Fájlok</th>
                    <th className="p-3 font-semibold">Foglalt Tárhely</th>
                    <th className="p-3 font-semibold">Közzétett Rekordok</th>
                    <th className="p-3 font-semibold">Felhasználók</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                  {perTenant.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-slate-500">
                        Nincs elérhető szervezeti statisztika.
                      </td>
                    </tr>
                  ) : (
                    perTenant.map((t: any) => (
                      <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                        <td className="p-3 font-bold text-slate-900 dark:text-white">{t.name}</td>
                        <td className="p-3 font-mono text-slate-500">/{t.slug}</td>
                        <td className="p-3 font-mono">{t.files || 0} db</td>
                        <td className="p-3 font-bold text-cyan-700 dark:text-cyan-300 font-mono">
                          {formatBytes(t.bytes)}
                        </td>
                        <td className="p-3 font-mono">{t.records_count || 0} db</td>
                        <td className="p-3 font-mono">{t.users_count || 0} fő</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
